import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiTrendingUp, FiUsers, FiTarget, FiSettings, FiUser } = FiIcons;

const SocialProofSection = () => {
  const testimonials = [
    {
      name: "Rajesh Kumar",
      title: "Manufacturing Unit Owner",
      company: "Kumar Steel Industries",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      text: "FunnelsDone transformed our lead generation. We now get 50+ qualified inquiries monthly without any manual effort. Our distributorship funnel alone brought us 15 new dealers!",
      rating: 5,
      results: "300% increase in leads",
      icon: FiSettings
    },
    {
      name: "Priya Sharma",
      title: "Business Consultant",
      company: "Sharma Consulting Services",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      text: "The WhatsApp automation is a game-changer! My consultation bookings increased by 400%. Clients love the instant responses and professional automated follow-ups.",
      rating: 5,
      results: "400% more bookings",
      icon: FiUser
    },
    {
      name: "Amit Patel",
      title: "Digital Marketing Freelancer",
      company: "Patel Digital Solutions",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      text: "I replaced 8 different tools with FunnelsDone. Now I save ₹25,000 monthly and my client results have improved dramatically. The portfolio funnel converts 35% of visitors!",
      rating: 5,
      results: "₹25k saved monthly",
      icon: FiTarget
    },
    {
      name: "Sunita Verma",
      title: "Coaching Business Owner",
      company: "Verma Life Coaching",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      text: "The automated webinar system helped me scale from 10 to 100+ students. The community feature keeps my clients engaged and coming back for more programs.",
      rating: 5,
      results: "10x student growth",
      icon: FiUsers
    },
    {
      name: "Vikram Singh",
      title: "Service Provider",
      company: "Singh Home Solutions",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
      text: "Our service inquiry process is now completely automated. From initial contact to appointment booking - everything happens automatically. Revenue increased by 250%!",
      rating: 5,
      results: "250% revenue growth",
      icon: FiTrendingUp
    },
    {
      name: "Neha Gupta",
      title: "E-commerce Store Owner",
      company: "Gupta Fashion Hub",
      image: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face",
      text: "The integrated e-commerce with WhatsApp automation boosted our sales by 180%. Customers love the seamless experience from product discovery to purchase completion.",
      rating: 5,
      results: "180% sales boost",
      icon: FiTarget
    }
  ];

  const logos = [
    { name: "Manufacturing", category: "Steel & Metal Industries" },
    { name: "Consulting", category: "Business & Strategy" },
    { name: "Digital Services", category: "Marketing & IT" },
    { name: "Education", category: "Coaching & Training" },
    { name: "Home Services", category: "Repair & Maintenance" },
    { name: "E-commerce", category: "Retail & Fashion" },
    { name: "Healthcare", category: "Clinics & Wellness" },
    { name: "Real Estate", category: "Property & Construction" }
  ];

  return (
    <section className="py-20 bg-gray-50 relative overflow-hidden">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Join <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">500+ Successful</span> MSMEs
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real businesses, real results. See how FunnelsDone has transformed MSMEs across India
          </p>

          {/* Overall Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">500+</div>
              <div className="text-gray-600">Happy MSMEs</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">300%</div>
              <div className="text-gray-600">Avg Growth</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">₹50L+</div>
              <div className="text-gray-600">Revenue Generated</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">24/7</div>
              <div className="text-gray-600">Support</div>
            </div>
          </div>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              {/* Rating */}
              <div className="flex items-center mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>

              {/* Testimonial Text */}
              <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.text}"</p>

              {/* Results Badge */}
              <div className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
                <SafeIcon icon={FiTrendingUp} className="w-4 h-4 mr-1" />
                {testimonial.results}
              </div>

              {/* Profile */}
              <div className="flex items-center">
                <img 
                  src={testimonial.image} 
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover mr-4"
                />
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                  <p className="text-sm text-gray-600">{testimonial.title}</p>
                  <p className="text-xs text-gray-500">{testimonial.company}</p>
                </div>
                <div className="ml-4">
                  <SafeIcon icon={testimonial.icon} className="w-8 h-8 text-blue-500" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Business Categories */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Trusted by MSMEs Across Industries</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {logos.map((logo, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900 mb-1">{logo.name}</div>
                  <div className="text-sm text-gray-600">{logo.category}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Join These Success Stories?</h3>
            <p className="text-lg mb-6 opacity-90">Get your personalized growth strategy and start seeing results within 30 days</p>
            <a 
              href="https://funnelsdone.com/cash" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-bold text-lg rounded-full hover:bg-gray-100 transition-all duration-300"
            >
              👉 Book Your Success Strategy Call
              <SafeIcon icon={FiTarget} className="ml-2 w-5 h-5" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SocialProofSection;